import { Badge, BlockStack, Box, Typography } from '@pv-frontend/ui-components-web';
import {
    FAIR_SHARE_COPY,
    atCopy,
    nextDayCopy,
    questionKindLabel,
    slotCopy,
    snoozeCopy,
    todayReasonCopy,
} from './home-feed-questions-copy';
import type { HomeFeedQuestionsView } from './home-feed-view';

export interface HomeFeedQuestionsProps {
    questions: HomeFeedQuestionsView;
    /** The instant the inspection was worked out for: "today" and "tomorrow" are relative to it. */
    evaluatedAt: string;
}

function Label({ children }: { children: string }) {
    return (
        <Typography variant="body-sm" fontWeight="medium" tone="text-secondary">
            {children}
        </Typography>
    );
}

/**
 * The Question card sections (PRD pv-platform#156): today's state with its reason, the countdown to the
 * next question day and its slot, the picked question with how much its answer would reorder, the other
 * candidates, and what the member has answered and snoozed. Read-only; every figure is the feed's own.
 */
export function HomeFeedQuestions({ questions, evaluatedAt }: HomeFeedQuestionsProps) {
    const { picked, others, nextDay } = questions;
    return (
        <BlockStack gap="300">
            <Box testID="home-feed-question-today">
                <BlockStack gap="100">
                    <Label>Question today</Label>
                    <Badge tone={questions.todayReason === 'OFFERED' ? 'success' : 'neutral'}>
                        {questions.todayReason === 'OFFERED' ? 'Offered' : 'Not offered'}
                    </Badge>
                    <Typography variant="body-md" testID="home-feed-question-reason">
                        {todayReasonCopy(questions.todayReason, questions.todayUntil, questions.snoozedUntil)}
                    </Typography>
                </BlockStack>
            </Box>

            <Box testID="home-feed-question-next-day">
                <BlockStack gap="100">
                    <Label>Next question day</Label>
                    <Typography variant="body-md">{nextDayCopy(nextDay, questions.scanDays, evaluatedAt)}</Typography>
                    {nextDay ? (
                        <Typography variant="body-sm" tone="text-secondary">
                            {slotCopy(nextDay.homesBefore)}
                        </Typography>
                    ) : null}
                    {nextDay?.prefersNonRanking ? (
                        <Typography variant="body-sm" tone="text-secondary">
                            {FAIR_SHARE_COPY}
                        </Typography>
                    ) : null}
                    {questions.pickedOnNextQuestionDay ? (
                        <Typography variant="body-sm" tone="text-secondary">
                            {`Based on today's homes, the question then would be ${questions.pickedOnNextQuestionDay}. A guide, not a promise: the homes will have moved.`}
                        </Typography>
                    ) : null}
                </BlockStack>
            </Box>

            <Box testID="home-feed-question-picked">
                <BlockStack gap="100">
                    <Label>Question that would be asked if one were offered now</Label>
                    {picked ? (
                        <>
                            <Typography variant="body-md" fontWeight="medium">
                                {picked.prompt}
                            </Typography>
                            <Typography variant="body-sm" tone="text-secondary">
                                {`${questionKindLabel(picked.kind)} · ${picked.key}`}
                            </Typography>
                            <Typography variant="body-sm" tone="text-secondary">
                                {`Would reorder up to ${picked.mostReordered} of the top ${picked.poolSize} homes (${picked.totalReordered} moved across all answers). Sits ${slotCopy(picked.afterHomes).toLowerCase()}`}
                            </Typography>
                            {picked.todayPrefersNonRanking ? (
                                <Typography variant="body-sm" tone="text-secondary">
                                    {`Today: ${FAIR_SHARE_COPY.charAt(0).toLowerCase()}${FAIR_SHARE_COPY.slice(1)}`}
                                </Typography>
                            ) : null}
                            {picked.answers.map((answer) => (
                                <Typography key={answer.label} variant="body-sm" tone="text-secondary">
                                    {`${answer.label}: moves ${answer.reordered}`}
                                </Typography>
                            ))}
                        </>
                    ) : (
                        <Typography variant="body-md" tone="text-secondary" testID="home-feed-question-no-pick">
                            No question to pick: the picker did not run or found nothing worth asking.
                        </Typography>
                    )}
                </BlockStack>
            </Box>

            {others.length > 0 ? (
                <Box testID="home-feed-question-others">
                    <BlockStack gap="100">
                        <Label>Other candidates, in order</Label>
                        {others.map((other, index) => (
                            <Typography key={other.key} variant="body-sm">
                                {`${index + 1}. ${other.prompt} · ${questionKindLabel(other.kind)} · up to ${other.mostReordered} moved`}
                            </Typography>
                        ))}
                    </BlockStack>
                </Box>
            ) : null}

            <Box testID="home-feed-question-history">
                <BlockStack gap="100">
                    <Label>Answers and snooze</Label>
                    <Typography variant="body-sm" testID="home-feed-question-snooze">
                        {snoozeCopy(questions.snoozedUntil, evaluatedAt)}
                    </Typography>
                    {questions.latestAnswers.length === 0 ? (
                        <Typography variant="body-sm" tone="text-secondary" testID="home-feed-question-no-answers">
                            No questions answered on this Search yet.
                        </Typography>
                    ) : (
                        questions.latestAnswers.map((answer) => (
                            <Typography key={answer.key} variant="body-sm" testID="home-feed-question-answer">
                                {`${answer.key}: ${answer.value === '' ? 'preference stated' : answer.value} · ${atCopy(answer.answeredUtc)}`}
                            </Typography>
                        ))
                    )}
                </BlockStack>
            </Box>
        </BlockStack>
    );
}
